/** Fuseau d'une société dont l'API n'a pas donné le sien (Company::DEFAULT_TIMEZONE) */
export const DEFAULT_TIMEZONE = 'Europe/Paris';

/**
 * Heure légale des sociétés : les lignes sont des instants UTC côté API (TimeEntry startAt / endAt), saisies et
 * affichées en date + heures à l'heure de la société (Company.timezone), quel que soit le fuseau du navigateur.
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Date et heure à la minute (en-CA, h23 : 2026-10-05, 18:00) ; fuseau inconnu : Europe/Paris */
function formatterOf(timezone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timezone);
  if (!formatter) {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    };
    try {
      formatter = new Intl.DateTimeFormat('en-CA', { ...options, timeZone: timezone });
    } catch {
      formatter = new Intl.DateTimeFormat('en-CA', { ...options, timeZone: DEFAULT_TIMEZONE });
    }
    formatters.set(timezone, formatter);
  }
  return formatter;
}

/** Date (2026-10-05) et heure (18:00) d'un instant, à l'heure de la société */
export function localOf(instant: Date | string, timezone: string): { date: string; time: string } {
  const parts = Object.fromEntries(
    formatterOf(timezone)
      .formatToParts(new Date(instant))
      .map((part) => [part.type, part.value]),
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

/** Jour courant (YYYY-MM-DD) à l'heure de la société : dernière date déclarable */
export function todayIn(timezone: string, now: Date = new Date()): string {
  return localOf(now, timezone).date;
}

/** Écart (ms) entre l'heure de la société et UTC à un instant donné */
function offsetAt(ms: number, timezone: string): number {
  const { date, time } = localOf(new Date(ms), timezone);
  return Date.parse(`${date}T${time}:00Z`) - Math.floor(ms / 60_000) * 60_000;
}

/**
 * Instant UTC (2026-10-05T16:00:00Z, format attendu par l'API) d'une date et d'une heure à l'heure de la société.
 * Heure vécue deux fois (retour à l'heure d'hiver) : la première ; heure sautée (passage à l'heure d'été) : décalée
 */
export function instantOf(date: string, time: string, timezone: string): string {
  const wall = Date.parse(`${date}T${time}:00Z`);
  const first = offsetAt(wall, timezone);
  let ms = wall - first;
  const second = offsetAt(ms, timezone);
  if (second !== first) ms = wall - second;
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** Fin d'une plage saisie : comme instantOf, mais une fin à minuit pile (00:00) clôt le jour, c'est le 00:00 du lendemain */
export function endInstantOf(date: string, time: string, timezone: string): string {
  if (time !== '00:00') return instantOf(date, time, timezone);
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return instantOf(next.toISOString().slice(0, 10), time, timezone);
}

/** Durée (minutes) d'une plage saisie, changement d'heure compris ; 0 si incomplète ou à l'envers */
export function rangeMinutes(date: string, start: string, end: string, timezone: string): number {
  if (!date || !start || !end) return 0;
  const minutes =
    (Date.parse(endInstantOf(date, end, timezone)) - Date.parse(instantOf(date, start, timezone))) / 60_000;
  return Math.max(0, minutes);
}
